import * as viemChains from 'viem/chains';
import { createPublicClient, createWalletClient, extractChain, http, serializeTransaction, } from 'viem';
import { loadLedgerServiceType } from '../lib.js';
export class LedgerEip1193Provider {
    ledger;
    derivationPath;
    address;
    chainId;
    constructor(ledger, params) {
        this.ledger = ledger;
        this.derivationPath = "m/44'/60'/0'/0/0";
        this.chainId = parseInt(params.chainId || '1');
    }
    async getClient() {
        const chain = extractChain({
            id: this.chainId,
            chains: Object.values(viemChains),
        });
        return createWalletClient({
            transport: http(),
            chain,
            account: (await this.getAddress()),
        });
    }
    async setChainId(chainId) {
        this.chainId = parseInt(chainId.replace('0x', ''), 16);
    }
    async getAddress() {
        if (!this.address) {
            const ledger = await this.ledger.getInstance();
            const { address } = await ledger.getAddress(this.derivationPath);
            this.address = address;
        }
        return this.address;
    }
    async signTypedData(data) {
        const ledgerInstance = await this.ledger.getInstance();
        const result = await ledgerInstance.signEIP712Message(this.derivationPath, JSON.parse(data));
        const combined = `${result.r}${result.s}${result.v.toString(16)}`;
        return combined.startsWith('0x') ? combined : `0x${combined}`;
    }
    async signTransaction(txData) {
        const ledgerInstance = await this.ledger.getInstance();
        const serializedTransaction = serializeTransaction(txData);
        const ledgerService = await loadLedgerServiceType();
        const resolution = await ledgerService.resolveTransaction(serializedTransaction.substring(2), {}, {});
        const signature = await ledgerInstance.signTransaction(this.derivationPath, serializedTransaction.substring(2), resolution);
        const signedTransaction = serializeTransaction(txData, {
            r: signature.r,
            s: signature.s,
            v: BigInt(signature.v),
        });
        return signedTransaction;
    }
    async signMessage(messageHex) {
        const ledgerInstance = await this.ledger.getInstance();
        const result = await ledgerInstance.signPersonalMessage(this.derivationPath, messageHex);
        const combined = `${result.r}${result.s}${result.v.toString(16)}`;
        return combined.startsWith('0x') ? combined : `0x${combined}`;
    }
    getChain() {
        return extractChain({
            id: this.chainId,
            chains: Object.values(viemChains),
        });
    }
    async request(args) {
        if (args.method === 'eth_requestAccounts') {
            const address = await this.getAddress();
            return [address];
        }
        if (args.method === 'eth_sign') {
            if (!args.params[0])
                throw new Error('Missing parameter for eth_sign');
            return this.signMessage(args.params[0]);
        }
        if (args.method === 'eth_signTransaction') {
            if (!args.params[0])
                throw new Error('Missing parameter for eth_signTransaction');
            return this.signTransaction(args.params[0]);
        }
        if (args.method === 'eth_signTypedData') {
            if (!args.params[0])
                throw new Error('Missing parameter for eth_signTypedData');
            return this.signTypedData(args.params[0]);
        }
        if (args.method === 'eth_chainId') {
            return `0x${this.chainId.toString(16)}`;
        }
        if (args.method === 'wallet_switchEthereumChain') {
            return this.setChainId(args.params[0]?.chainId || '0x1'); // TODO: fallback to wallet strategy chainid
        }
        if (args.method === 'eth_estimateGas') {
            const client = createPublicClient({
                chain: this.getChain(),
                transport: http(),
            });
            const data = {
                to: args.params[0].to,
                value: args.params[0].value,
                data: args.params[0].data,
                account: (await this.getAddress()),
            };
            const estimate = await client.estimateGas(data);
            return `0x${estimate.toString(16)}`;
        }
        if (args.method === 'eth_getTransactionCount') {
            if (!args.params) {
                throw new Error('params is required');
            }
            const client = createPublicClient({
                chain: this.getChain(),
                transport: http(),
            });
            const count = await client.getTransactionCount({
                address: (await this.getAddress()),
                blockTag: 'pending',
            });
            return `0x${count.toString(16)}`;
        }
        if (args.method === 'eth_sendTransaction') {
            const address = await this.getAddress();
            const walletClient = createWalletClient({
                transport: http(),
                chain: this.getChain(),
                account: address,
            });
            const preparedTransaction = await walletClient.prepareTransactionRequest({
                ...args.params[0],
            });
            const signedTransaction = await this.signTransaction(preparedTransaction);
            const tx = await walletClient.sendRawTransaction({
                serializedTransaction: signedTransaction,
            });
            return tx;
        }
        const client = await this.getClient();
        return client.request({
            method: args.method,
            params: args.params,
        });
    }
    on(_event, _listener) {
        throw new Error('Method not implemented.');
    }
    once(_event, _listener) {
        throw new Error('Method not implemented.');
    }
    removeListener(_event, _listener) {
        throw new Error('Method not implemented.');
    }
    off(_event, _listener) {
        throw new Error('Method not implemented.');
    }
}
